import { useAuth0 } from '@auth0/auth0-react';
import { useContext, useEffect, useRef, useState } from 'react';
import React from 'react';
import { FiExternalLink, FiGlobe, FiMoreVertical } from 'react-icons/fi';

import { myhiveLocalization, MyHiveLocalizationKey } from '../localization/my-hive.js';
import { LanguageContext } from '../providers/LanguageProvider.js';
import { type Language } from '../utils/localization.js';

interface Props {
  children: JSX.Element;
}

export default function TopNavBar({ children }: Props): React.JSX.Element {
  const { loginWithRedirect } = useAuth0();
  const { language, setLanguage } = useContext(LanguageContext);
  const t = myhiveLocalization[language];

  const [menuOpen, setMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const languageButtonRef = useRef<HTMLButtonElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        menuOpen &&
        menuRef.current &&
        menuButtonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }

      if (
        languageMenuOpen &&
        languageMenuRef.current &&
        languageButtonRef.current &&
        !languageMenuRef.current.contains(event.target as Node) &&
        !languageButtonRef.current.contains(event.target as Node)
      ) {
        setLanguageMenuOpen(false);
      }

      if (
        profileMenuOpen &&
        profileMenuRef.current &&
        profileButtonRef.current &&
        !profileMenuRef.current.contains(event.target as Node) &&
        !profileButtonRef.current.contains(event.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen, languageMenuOpen, profileMenuOpen]);

  const toggleMenu = (): void => setMenuOpen(!menuOpen);
  const toggleLanguageMenu = (): void => setLanguageMenuOpen(!languageMenuOpen);

  const handleLanguageSelect = (languageCode: Language): void => {
    setLanguage(languageCode);
    setLanguageMenuOpen(false);
  };

  const handleSignIn = (): void => {
    void loginWithRedirect({
      appState: { returnTo: window.location.href },
      authorizationParams: {
        ui_locales: language === 'fr' ? 'fr-CA' : 'en',
      },
    });
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-white shadow-[0_1px_3px_-1px_rgba(0,0,0,0.8)] z-50">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center">
            <div className="relative">
              <button
                ref={menuButtonRef}
                onClick={toggleMenu}
                className="cursor-pointer p-2 rounded-full hover:bg-gray-100"
                aria-label="Menu"
              >
                <FiMoreVertical style={{ marginTop: 2 }} size={22} className="text-gray-800" />
              </button>
              {menuOpen && (
                <div
                  ref={menuRef}
                  className="absolute left-0 mt-2 w-56 bg-white rounded-md overflow-hidden shadow-[0_2px_8px_-1px_rgba(0,0,0,0.8)] py-1 z-51"
                >
                  <div className="px-4 py-2 font-medium text-gray-500">{t[MyHiveLocalizationKey.MenuHeading]}</div>
                  <button
                    onClick={() => {
                      window.open('https://u89fw2m3i66.typeform.com/to/Jvl1ZDZN', '_blank');
                      setMenuOpen(false);
                    }}
                    className="cursor-pointer flex items-center w-full text-left px-4 py-2 text-md font-medium text-gray-800 hover:bg-gray-100"
                  >
                    <FiExternalLink className="mr-2 text-gray-800" style={{ marginTop: 2 }} />{' '}
                    {t[MyHiveLocalizationKey.HowCanWeHelp]}
                  </button>
                  <button
                    onClick={() => {
                      window.open('https://www.alveole.buzz', '_blank');
                      setMenuOpen(false);
                    }}
                    className="cursor-pointer flex items-center w-full text-left px-4 py-2 text-md font-medium text-gray-800 hover:bg-gray-100"
                  >
                    <FiExternalLink className="mr-2 text-gray-800" style={{ marginTop: 2 }} />{' '}
                    {t[MyHiveLocalizationKey.Website]}
                  </button>
                  <button
                    onClick={() => {
                      window.open('https://www.alveole.buzz/blog/', '_blank');
                      setMenuOpen(false);
                    }}
                    className="cursor-pointer flex items-center w-full text-left px-4 py-2 text-md font-medium text-gray-800 hover:bg-gray-100"
                  >
                    <FiExternalLink className="mr-2 text-gray-800" style={{ marginTop: 2 }} />{' '}
                    {t[MyHiveLocalizationKey.Blog]}
                  </button>
                  <button
                    onClick={() => {
                      window.open(t[MyHiveLocalizationKey.FaqLink], '_blank');
                    }}
                    className="cursor-pointer flex items-center w-full text-left px-4 py-2 text-md font-medium text-gray-800 hover:bg-gray-100"
                  >
                    <FiExternalLink className="mr-2 text-gray-800" style={{ marginTop: 2 }} /> Faq
                  </button>
                </div>
              )}
            </div>
            <h1 style={{ letterSpacing: '2px' }} className="text-lg font-medium text-gray-800 ml-8">
              {t[MyHiveLocalizationKey.AppTitle]}
            </h1>
          </div>

          <div className="flex items-center space-x-2">
            <div className="relative">
              <button
                ref={languageButtonRef}
                onClick={toggleLanguageMenu}
                className="cursor-pointer p-2 rounded-full hover:bg-gray-100 "
                aria-label="Language"
              >
                <FiGlobe size={22} className="text-gray-800" />
              </button>

              {languageMenuOpen && (
                <div
                  ref={languageMenuRef}
                  className="absolute right-0 mt-2 w-48 bg-white rounded-md overflow-hidden shadow-[0_2px_8px_-1px_rgba(0,0,0,0.8)] py-1 z-50"
                >
                  <button
                    onClick={() => handleLanguageSelect('en')}
                    className={`cursor-pointer block w-full text-left px-4 py-2 text-md font-medium ${language === 'en' ? 'bg-gray-100 text-gray-900' : 'text-gray-800'} hover:bg-gray-100`}
                  >
                    {t[MyHiveLocalizationKey.English]}
                  </button>
                  <button
                    onClick={() => handleLanguageSelect('fr')}
                    className={`cursor-pointer block w-full text-left px-4 py-2 text-md font-medium ${language === 'fr' ? 'bg-gray-100 text-gray-900' : 'text-gray-800'} hover:bg-gray-100`}
                  >
                    {t[MyHiveLocalizationKey.French]}
                  </button>
                </div>
              )}
            </div>
            <button onClick={handleSignIn} className="cursor-pointer w-full  text-md font-medium text-gray-800">
              {t[MyHiveLocalizationKey.SignIn]}
            </button>
          </div>
        </div>
      </header>

      <main className="pt-16 min-h-screen bg-[#F0ECE5]">{children}</main>
    </>
  );
}
